import { CAMPAIGN_STATUSES, type CampaignLinkStatus } from "@/constants/campaings";
import { GIFT_LINKS_COPY } from "@/constants/giftLinks";
import type { CampaignContactWire } from "@/interfaces/campaignContacts";
import { buildCsv, type CsvColumn } from "@/lib/csv";
import type { CampaignStatus } from "@/types/types";
import { formatUsd } from "@/utils/number-format";
import { formatContactDate, formatSendInstant } from "../contacts/campaignContacts-helper";
import type {
  GiftLinksAddBlock,
  GiftLinksAddGate,
  GiftLinksAddGateInput,
  GiftLinksCost,
  GiftLinkViewRow,
} from "./types";

const CENTS = 100;

/** Wire statuses that stay as they are in every campaign status. */
const TERMINAL_STATUSES: readonly CampaignLinkStatus[] = [
  CAMPAIGN_STATUSES.LINK_REDEEMED,
  CAMPAIGN_STATUSES.LINK_DECLINED,
];

/**
 * Campaign statuses whose links no longer work: closed (expired), and the terminal overlays the
 * server's redemption gate kills every link of (canceled, archived, deleted). Paused is not here:
 * a paused campaign's links still redeem.
 */
export const ENDED_CAMPAIGN_STATUSES: readonly CampaignStatus[] = [
  CAMPAIGN_STATUSES.CLOSED,
  CAMPAIGN_STATUSES.CANCELED,
  CAMPAIGN_STATUSES.ARCHIVED,
  CAMPAIGN_STATUSES.DELETED,
];

function viewStatus(
  status: CampaignLinkStatus,
  campaignStatus: CampaignStatus,
): CampaignLinkStatus {
  if (TERMINAL_STATUSES.includes(status)) return status;
  return ENDED_CAMPAIGN_STATUSES.includes(campaignStatus)
    ? CAMPAIGN_STATUSES.LINK_EXPIRED
    : CAMPAIGN_STATUSES.LINK_PENDING;
}

/** The envelope's rows as table rows: numbered by position; Expired once ended (AM3), else Pending. */
export function toGiftLinkRows(
  contacts: readonly CampaignContactWire[],
  campaignStatus: CampaignStatus,
): GiftLinkViewRow[] {
  return contacts.map((c, i) => ({
    id: c.id,
    number: i + 1,
    url: c.redemptionLink,
    status: viewStatus(c.status, campaignStatus),
    redeemedAt: c.redeemedAt,
  }));
}

/** Search (trimmed, case-insensitive URL substring) intersected with the status; numbers are kept. */
export function filterGiftLinks(
  rows: readonly GiftLinkViewRow[],
  search: string,
  status: CampaignLinkStatus | null,
): GiftLinkViewRow[] {
  const needle = search.trim().toLowerCase();
  return rows.filter((r) => {
    if (status !== null && r.status !== status) return false;
    if (needle === "") return true;
    return r.url?.toLowerCase().includes(needle) === true;
  });
}

/** Every non-null URL, newline-joined. */
export function copyAllLinksText(rows: readonly GiftLinkViewRow[]): string {
  return rows.flatMap((r) => (r.url === null ? [] : [r.url])).join("\n");
}

/**
 * Sources for the modal's two export actions. Copy all copies every link (D6), regardless of the
 * active search or status filter; Export CSV exports the shown rows (`filtered`).
 */
export function giftLinksExportSources(
  all: readonly GiftLinkViewRow[],
  filtered: readonly GiftLinkViewRow[],
): { copyAllText: string; csvRows: readonly GiftLinkViewRow[] } {
  return { copyAllText: copyAllLinksText(all), csvRows: filtered };
}

/** Copy all / Export CSV: a viewer gets no links and may take no action in the modal. */
export function giftLinksExportGate(
  canEdit: boolean,
  count: number,
  emptyReason: string | null = null,
): { disabled: boolean; reason: string | null } {
  if (!canEdit) return { disabled: true, reason: GIFT_LINKS_COPY.exportBlocked };
  return count === 0 ? { disabled: true, reason: emptyReason } : { disabled: false, reason: null };
}

const [HEADER_NUMBER, HEADER_LINK, HEADER_STATUS, HEADER_REDEEMED] = GIFT_LINKS_COPY.csvHeaders;

export function buildGiftLinksCsv(rows: readonly GiftLinkViewRow[], timeZone: string): string {
  const columns: CsvColumn<GiftLinkViewRow>[] = [
    { label: HEADER_NUMBER, value: (r) => String(r.number) },
    { label: HEADER_LINK, value: (r) => r.url ?? "" },
    { label: HEADER_STATUS, value: (r) => r.status },
    {
      label: HEADER_REDEEMED,
      value: (r) => (r.redeemedAt ? formatSendInstant(r.redeemedAt, timeZone) : ""),
    },
  ];
  return buildCsv(columns, [...rows]);
}

/** `campaign-<id>[-<status lowercased>]-gift-links.csv`. */
export function giftLinksCsvFilename(
  campaignId: string,
  status: CampaignLinkStatus | null,
): string {
  const middle = status === null ? "" : `-${status.toLowerCase()}`;
  return `campaign-${campaignId}${middle}-gift-links.csv`;
}

export function giftLinksSubtitle(name: string, shown: number, total: number): string {
  return shown === total
    ? GIFT_LINKS_COPY.subtitle.all(name, total)
    : GIFT_LINKS_COPY.subtitle.filtered(name, shown, total);
}

export interface GiftLinksBannerInput {
  scheduledDate: string | null;
  launchedAt: string | null;
  now: Date;
  timeZone: string;
}

/** "Links activate <date>" / "Links active since <date>" / the fallback, then the distribute sentence. */
export function giftLinksBanner(i: GiftLinksBannerInput): string {
  const { banner } = GIFT_LINKS_COPY;
  const instant = i.scheduledDate ?? i.launchedAt;
  if (instant === null) return `${banner.fallback}${banner.distribute}`;
  const date = formatContactDate(instant, i.timeZone);
  const lead =
    new Date(instant).getTime() > i.now.getTime()
      ? banner.activates(date)
      : banner.activeSince(date);
  return `${lead}${banner.join}${banner.distribute}`;
}

/** The Redeemed cell: the instant in the campaign's zone, "—" when null. */
export function formatRedeemedAt(iso: string | null, timeZone: string): string {
  return iso ? formatSendInstant(iso, timeZone) : GIFT_LINKS_COPY.emptyValue;
}

/** Free spaces are used first; `total` = paid × price, rounded to cents. */
export function giftLinksCost(
  qty: number,
  spacesFree: number,
  pricePerGift: number,
): GiftLinksCost {
  const free = Math.min(qty, Math.max(0, spacesFree));
  const paid = qty - free;
  return { free, paid, total: Math.round(paid * pricePerGift * CENTS) / CENTS };
}

/** "[F into free space(s) · ]P × $X = $T". */
export function giftLinksCostLine(qty: number, spacesFree: number, pricePerGift: number): string {
  const { free, paid, total } = giftLinksCost(qty, spacesFree, pricePerGift);
  const prefix = free > 0 ? GIFT_LINKS_COPY.wizard.costFree(free) : "";
  return `${prefix}${paid} × ${formatUsd(pricePerGift)} = ${formatUsd(total)}`;
}

/**
 * First matching block: viewer, standard_send, draft, closed (any ended status), paused (the flag
 * or the server's paused status), multi_use, else no_budget.
 */
export function giftLinksAddGate(i: GiftLinksAddGateInput): GiftLinksAddGate {
  const block = firstAddBlock(i);
  if (block === null) return { allowed: true, block: null, reason: null };
  return { allowed: false, block, reason: GIFT_LINKS_COPY.addBlocked[block] };
}

function firstAddBlock(i: GiftLinksAddGateInput): GiftLinksAddBlock | null {
  if (!i.canEdit) return "viewer";
  if (!i.manual) return "standard_send";
  if (i.status === CAMPAIGN_STATUSES.DRAFT) return "draft";
  if (ENDED_CAMPAIGN_STATUSES.includes(i.status)) return "closed";
  if (i.paused || i.status === CAMPAIGN_STATUSES.PAUSED) return "paused";
  if (i.multiUse) return "multi_use";
  if (!i.canAddGiftLinks) return "no_budget";
  if (i.noFreeSpace) return "no_free_space";
  return null;
}
